import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/api';

/* ───────────────────── helpers ───────────────────── */
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const LEAVE_TYPES = ['Public Holiday', 'Company Holiday', 'Planned Shutdown'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function isoDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

/* ═══════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════ */
export default function CompanyCalendar() {
  const now = new Date();
  const [year, setYear]       = useState(now.getFullYear());
  const [month, setMonth]     = useState(now.getMonth());
  const [entries, setEntries] = useState([]);
  const [quota, setQuota]     = useState(26);
  const [loading, setLoading] = useState(true);

  /* modal state */
  const [modal, setModal]               = useState(null); // null | { date, entry? }
  const [modalType, setModalType]       = useState(LEAVE_TYPES[0]);
  const [modalDesc, setModalDesc]       = useState('');
  const [modalRecurring, setModalRecurring] = useState(false);
  const [modalSaving, setModalSaving]   = useState(false);
  const [modalError, setModalError]     = useState('');

  /* quota edit */
  const [quotaInput, setQuotaInput]     = useState('');
  const [quotaSaving, setQuotaSaving]   = useState(false);

  /* ── Fetch year data ── */
  const fetchYear = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get(`/calendar?year=${year}`);
      setEntries(data.entries || []);
      setQuota(data.quota ?? 26);
      setQuotaInput(String(data.quota ?? 26));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchYear(); }, [fetchYear]);

  /* ── Lookup map: dateISO → entry ── */
  const entryMap = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const d = e.date instanceof Date ? e.date.toISOString().slice(0, 10)
        : typeof e.date === 'string' ? e.date.slice(0, 10) : '';
      if (d) map[d] = e;
    }
    return map;
  }, [entries]);

  /* ── Year totals for summary ── */
  const yearTotals = useMemo(() => {
    const counts = { 'Public Holiday': 0, 'Company Holiday': 0, 'Planned Shutdown': 0 };
    for (const e of entries) {
      if (counts[e.leave_type] !== undefined) counts[e.leave_type]++;
    }
    const total = entries.length;
    return { ...counts, total };
  }, [entries]);

  const overQuota = yearTotals.total > quota;

  /* ── Month nav ── */
  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  /* ── Calendar grid data ── */
  const calGrid = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const total = daysInMonth(year, month);
    const cells = [];
    // leading blanks
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    // trailing blanks to fill last row
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  /* ── Day click → open modal ── */
  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = isoDate(year, month, day);
    const existing = entryMap[dateStr];
    setModalError('');
    if (existing) {
      setModalType(existing.leave_type || LEAVE_TYPES[0]);
      setModalDesc(existing.description || '');
      setModalRecurring(!!existing.is_recurring);
      setModal({ date: dateStr, entry: existing });
    } else {
      setModalType(LEAVE_TYPES[0]);
      setModalDesc('');
      setModalRecurring(false);
      setModal({ date: dateStr, entry: null });
    }
  };

  /* ── Save / Delete from modal ── */
  const handleModalSave = async () => {
    setModalSaving(true); setModalError('');
    try {
      if (modal.entry) {
        await api.put(`/calendar/${modal.date}`, {
          leave_type: modalType, description: modalDesc, is_recurring: modalRecurring ? 1 : 0,
        });
      } else {
        await api.post('/calendar', {
          date: modal.date, leave_type: modalType, description: modalDesc, is_recurring: modalRecurring ? 1 : 0,
        });
      }
      await fetchYear();
      setModal(null);
    } catch (e) { setModalError(e.message); }
    finally { setModalSaving(false); }
  };

  const handleModalDelete = async () => {
    if (!modal?.entry) return;
    setModalSaving(true); setModalError('');
    try {
      await api.del(`/calendar/${modal.date}`);
      await fetchYear();
      setModal(null);
    } catch (e) { setModalError(e.message); }
    finally { setModalSaving(false); }
  };

  /* ── Save quota ── */
  const handleQuotaSave = async () => {
    const val = parseInt(quotaInput);
    if (isNaN(val) || val < 0) return;
    setQuotaSaving(true);
    try {
      await api.put('/settings', { total_leave_days_per_year: val });
      setQuota(val);
    } catch { /* silent */ }
    finally { setQuotaSaving(false); }
  };

  /* ── Day cell styling ── */
  const dayCell = (day) => {
    if (!day) return 'bg-transparent';
    const dateStr = isoDate(year, month, day);
    const d = new Date(year, month, day);
    const dow = d.getDay();
    const entry = entryMap[dateStr];
    const isToday = dateStr === todayISO();

    let bg = 'bg-white hover:bg-sap-blue/5'; // working day
    let text = 'text-sap-text';
    let ring = '';

    if (dow === 0 || dow === 6) {
      bg = 'bg-[#F0F4F3]'; text = 'text-[#6B7F7F]';
    }
    if (entry) {
      bg = 'bg-sap-blueLight hover:bg-sap-blueMuted'; text = 'text-sap-blue';
    }
    if (isToday) ring = 'ring-2 ring-sap-blue ring-inset';

    return `${bg} ${text} ${ring}`;
  };

  return (
    <div className="space-y-3">
      <h1 className="page-title">Company Calendar</h1>

      <div className="flex gap-4">
        {/* ══════ Left: Calendar Grid ══════ */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm">
          {/* Month nav */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-sap-border/60">
            <button onClick={prevMonth} className="px-2 py-0.5 text-xs border border-sap-border rounded-lg hover:bg-sap-blue/5">‹</button>
            <div className="flex items-center gap-2">
              <span className="page-title">{MONTHS[month]} {year}</span>
              <select value={year} onChange={e => setYear(Number(e.target.value))}
                className="h-6 px-1 text-xs border border-sap-border rounded bg-white
                           focus:outline-none focus:border-sap-blue">
                {Array.from({ length: 11 }, (_, i) => now.getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button onClick={nextMonth} className="px-2 py-0.5 text-xs border border-sap-border rounded-lg hover:bg-sap-blue/5">›</button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7">
            {DAYS.map(d => (
              <div key={d} className="text-center text-2xs font-semibold text-sap-textLight py-1.5 border-b border-sap-border/60 bg-sap-blue/5">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-5 h-5 border-2 border-sap-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {calGrid.map((day, i) => {
                const dateStr = day ? isoDate(year, month, day) : '';
                const entry = day ? entryMap[dateStr] : null;
                return (
                  <button key={i} disabled={!day}
                    onClick={() => handleDayClick(day)}
                    className={`relative h-20 border-b border-r border-gray-100 p-1 text-left transition-colors
                      ${dayCell(day)}
                      ${!day ? 'cursor-default' : 'cursor-pointer'}`}>
                    {day && (
                      <>
                        <span className="text-xs font-medium">{day}</span>
                        {entry && (
                          <div className="mt-0.5">
                            <span className={`inline-block px-1 py-0.5 rounded text-2xs font-medium leading-tight truncate max-w-full
                              ${entry.leave_type === 'Public Holiday' ? 'bg-sap-blue/20 text-sap-blue' :
                                entry.leave_type === 'Company Holiday' ? 'bg-[#7B6FA0]/20 text-[#7B6FA0]' :
                                'bg-[#B8860B]/20 text-[#B8860B]'}`}>
                              {entry.leave_type === 'Public Holiday' ? 'PH' :
                               entry.leave_type === 'Company Holiday' ? 'CH' : 'PS'}
                            </span>
                            <p className="text-2xs text-sap-blue truncate mt-0.5 leading-tight">{entry.description}</p>
                            {!!entry.is_recurring && <span className="text-2xs text-sap-blue/60">↻</span>}
                          </div>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 px-4 py-2 border-t border-sap-border text-2xs text-sap-textLight">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-[#DDD9D1]" /> Working Day</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sap-blueLight border border-sap-blue/20" /> Holiday / Leave</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F0F4F3] border border-[#DDD9D1]" /> Weekend</span>
            <span className="flex items-center gap-1"><span className="text-xs">↻</span> Recurring</span>
          </div>
        </div>

        {/* ══════ Right: Leave Summary ══════ */}
        <div className="w-64 shrink-0 space-y-3">
          {/* Quota */}
          <div className="bg-white rounded-2xl shadow-sm p-3 space-y-2">
            <h3 className="text-xs font-semibold text-sap-text">Leave Quota</h3>
            <div className="flex items-center gap-2">
              <label className="text-2xs text-sap-textLight shrink-0">Days / Year</label>
              <input value={quotaInput} onChange={e => setQuotaInput(e.target.value)}
                type="number" min="0"
                className="flex-1 h-6 px-2 text-xs border border-sap-border rounded
                           focus:outline-none focus:border-sap-blue" />
              <button onClick={handleQuotaSave} disabled={quotaSaving}
                className="px-2 py-1 text-2xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark disabled:opacity-50">
                {quotaSaving ? '…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className={`rounded-2xl shadow-sm p-3 space-y-2 ${overQuota ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-white'}`}>
            <h3 className="text-xs font-semibold text-sap-text">
              {year} Leave Summary
            </h3>
            <SummaryRow label="Public Holidays"  count={yearTotals['Public Holiday']}  color="bg-sap-blue" />
            <SummaryRow label="Company Holidays" count={yearTotals['Company Holiday']} color="bg-[#7B6FA0]" />
            <SummaryRow label="Planned Shutdowns" count={yearTotals['Planned Shutdown']} color="bg-[#B8860B]" />
            <div className="border-t border-sap-border pt-1.5 mt-1.5">
              <div className="flex justify-between text-xs">
                <span className="font-semibold text-sap-text">Total Marked</span>
                <span className={`font-bold ${overQuota ? 'text-red-600' : 'text-sap-blue'}`}>
                  {yearTotals.total} / {quota}
                </span>
              </div>
              {overQuota && (
                <p className="text-2xs text-red-600 mt-1 font-medium">
                  ⚠ Exceeds quota by {yearTotals.total - quota} day{yearTotals.total - quota !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-[#DDD9D1] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${overQuota ? 'bg-red-500' : 'bg-sap-blue'}`}
                style={{ width: `${Math.min((yearTotals.total / Math.max(quota, 1)) * 100, 100)}%` }} />
            </div>
          </div>

          {/* MRP note */}
          <div className="bg-sap-blueLight rounded-2xl shadow-sm p-3">
            <h3 className="text-xs font-semibold text-sap-blue mb-1">MRP Integration</h3>
            <p className="text-2xs text-sap-blue/80 leading-relaxed">
              The MRP Wizard automatically skips all non-working days (holidays + weekends) when calculating lead times.
              <br/><br/>
              <strong>Adjusted Lead Time</strong> = Raw Lead Time + non-working days within the window.
            </p>
          </div>
        </div>
      </div>

      {/* ══════ Modal ══════ */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
             onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-96 max-w-[90vw]"
               onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-sap-border">
              <h2 className="text-xs font-semibold text-sap-text">
                {modal.entry ? 'Edit Leave Day' : 'Mark as Non-Working Day'}
              </h2>
              <button onClick={() => setModal(null)} className="text-sap-textLight hover:text-sap-text">✕</button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
              {modalError && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{modalError}</p>}

              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-sap-textLight text-right">Date</label>
                <span className="text-xs font-medium text-sap-text">{modal.date}</span>
              </div>

              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-sap-textLight text-right">Leave Type</label>
                <select value={modalType} onChange={e => setModalType(e.target.value)}
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded bg-white
                             focus:outline-none focus:border-sap-blue">
                  {LEAVE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-sap-textLight text-right">Description</label>
                <input value={modalDesc} onChange={e => setModalDesc(e.target.value)}
                  placeholder="e.g. Independence Day"
                  className="flex-1 h-7 px-2 text-xs border border-sap-border rounded
                             focus:outline-none focus:border-sap-blue" />
              </div>

              <div className="flex items-center gap-2">
                <label className="w-28 text-xs text-sap-textLight text-right">Recurring?</label>
                <label className="flex items-center gap-1.5 text-xs text-sap-text cursor-pointer">
                  <input type="checkbox" checked={modalRecurring}
                    onChange={e => setModalRecurring(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-sap-border text-sap-blue" />
                  Repeat every year on this date
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-sap-border bg-sap-bg rounded-b-2xl">
              <div>
                {modal.entry && (
                  <button onClick={handleModalDelete} disabled={modalSaving}
                    className="px-3 py-1 text-xs text-red-600 border border-red-300 rounded-lg
                               hover:bg-red-50 disabled:opacity-50 transition-colors">
                    Remove
                  </button>
                )}
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setModal(null)}
                  className="px-3 py-1 text-xs border border-sap-border rounded-lg hover:bg-sap-blue/5 transition-colors">
                  Cancel
                </button>
                <button onClick={handleModalSave} disabled={modalSaving}
                  className="px-4 py-1 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark
                             disabled:opacity-50 transition-colors font-semibold">
                  {modalSaving ? 'Saving…' : modal.entry ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Summary row ── */
function SummaryRow({ label, count, color }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded ${color}`} />
        <span className="text-sap-textLight">{label}</span>
      </span>
      <span className="font-medium text-sap-text">{count}</span>
    </div>
  );
}
